import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Users, Lock, Smartphone, AlertCircle, Loader, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { ASTERPOS_LOGO } from '@/config/brandAssets';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Staff sign-in = the staff PIN door (backend /api/rest/v1/pos/auth/*), the same
// one the POS uses: phone -> texted code (new device) -> PIN. The answer is a
// staff token (typ "staff") naming the staff member's restaurant and role, kept
// as pos_token for the staff pages (/staff/schedule, /staff/availability) and
// the dining screens. A customer sign-in is never a staff session.
const deviceId = () => {
  let id = null;
  try { id = localStorage.getItem('pos_device_id'); } catch { id = null; }
  if (!id) {
    id = `web_staff_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    try { localStorage.setItem('pos_device_id', id); } catch { /* private mode */ }
  }
  return id;
};

const stored = (key) => { try { return localStorage.getItem(key); } catch { return null; } };

async function post(path, body) {
  const res = await fetch(`${API}/api/rest/v1/pos/auth/${path}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  let data = {};
  try { data = await res.json(); } catch { data = {}; }
  return { ok: res.ok, data };
}

const errorText = (data, fallback) => {
  const d = data?.detail;
  if (typeof d === 'string') return d;
  return data?.message || fallback;
};

export default function StaffLoginPage() {
  const navigate = useNavigate();
  const trustedPhone = stored('pos_device_phone');
  const trustedToken = stored('pos_device_token');
  const [mode, setMode] = useState(trustedPhone && trustedToken ? 'pin_trusted' : 'phone');
  const [phone, setPhone] = useState(trustedPhone || '');
  const [code, setCode] = useState('');
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const signedIn = (data, usedPhone) => {
    try {
      localStorage.setItem('pos_token', data.token);
      localStorage.setItem('pos_user', JSON.stringify(data.employee || {}));
      if (data.device_token) {
        localStorage.setItem('pos_device_token', data.device_token);
        localStorage.setItem('pos_device_phone', usedPhone);
      }
    } catch { /* storage blocked: the session lasts this page only */ }
    // ?next=/dining/... (the Restaurant OS sign-in link) returns there; nothing else is followed
    const next = new URLSearchParams(window.location.search).get('next') || '';
    navigate(/^\/dining(\/|$|\?)/.test(next) ? next : '/staff/schedule', { replace: true });
  };

  const run = async (fn) => {
    setBusy(true);
    setError('');
    try { await fn(); } catch { setError('Network error. Please try again.'); } finally { setBusy(false); }
  };

  const sendCode = (e) => {
    e.preventDefault();
    run(async () => {
      const { ok, data } = await post('request-login-otp', { phone, device_id: deviceId() });
      if (ok && data.success) setMode('code');
      else setError(errorText(data, 'Could not send the code'));
    });
  };

  const checkCode = (e) => {
    e.preventDefault();
    run(async () => {
      const { ok, data } = await post('verify-login-otp', { phone, otp_code: code, device_id: deviceId() });
      // an allowed phone's first sign-in sets its PIN (POS Login Access)
      if (ok && data.success) { setMode(data.requires_pin_setup ? 'pin_setup' : 'pin_new'); setCode(''); }
      else setError(errorText(data, 'That code is not right'));
    });
  };

  const signInWithPin = (e) => {
    e.preventDefault();
    run(async () => {
      const body = mode === 'pin_trusted'
        ? { phone: trustedPhone, pin, device_id: deviceId(), device_token: trustedToken }
        : { phone, pin, device_id: deviceId(), device_name: 'Staff pages', device_type: 'web' };
      const { ok, data } = await post('login-pin', body);
      if (ok && data.success && data.token) return signedIn(data, mode === 'pin_trusted' ? trustedPhone : phone);
      if (data.requires_otp) {
        setMode('phone');
        setError('This device needs your phone verified again.');
        return;
      }
      setPin('');
      setError(errorText(data, 'Wrong PIN'));
    });
  };

  const setFirstPin = (e) => {
    e.preventDefault();
    if (pin !== pin2) { setError('The two PINs do not match'); return; }
    run(async () => {
      const { ok, data } = await post('set-first-pin', { phone, pin, confirm_pin: pin2, device_id: deviceId(),
                                                          device_name: 'Staff pages', device_type: 'web' });
      if (ok && data.success && data.token) return signedIn(data, phone);
      if (data.requires_otp) { setMode('phone'); setError('Please verify your phone again.'); return; }
      setError(errorText(data, 'Could not set the PIN'));
    });
  };

  const useAnotherPhone = () => {
    try { localStorage.removeItem('pos_device_token'); localStorage.removeItem('pos_device_phone'); } catch { /* ignore */ }
    setPhone('');
    setPin('');
    setMode('phone');
  };

  const field = 'bg-white/10 border-white/20 text-white placeholder:text-slate-400';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="border-b border-white/10 bg-black/20 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={ASTERPOS_LOGO} alt="Chain29" className="h-8 w-8" />
            <span className="text-xl font-bold text-white">Chain29</span>
          </Link>
          <Link to="/owner/login">
            <Button variant="ghost" className="text-white hover:text-white hover:bg-white/10 text-sm">
              Restaurant Owner?
            </Button>
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 py-10 max-w-md">
        <div className="flex items-center gap-2 text-slate-400 text-sm mb-6">
          <Users className="w-4 h-4" />
          <span>Staff sign-in</span>
        </div>

        <div className="rounded-2xl bg-white/5 border border-white/10 p-6 space-y-5" data-testid="staff-login">
          {mode === 'phone' && (
            <form onSubmit={sendCode} className="space-y-4">
              <p className="text-slate-300 text-sm">Enter the phone number your manager has on file. We text you a code.</p>
              <div>
                <Label className="text-slate-200 flex items-center gap-2 mb-2"><Smartphone className="w-4 h-4" />Phone</Label>
                <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required autoFocus
                       placeholder="(555) 123-4567" className={field} data-testid="staff-phone" />
              </div>
              <Button type="submit" disabled={busy || !phone} className="w-full">
                {busy ? <Loader className="w-4 h-4 animate-spin" /> : 'Text me a code'}
              </Button>
            </form>
          )}

          {mode === 'code' && (
            <form onSubmit={checkCode} className="space-y-4">
              <p className="text-slate-300 text-sm">Enter the code we texted to {phone}.</p>
              <Input inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                     required autoFocus maxLength={6} className={`${field} text-center text-2xl tracking-widest`}
                     data-testid="staff-code" />
              <Button type="submit" disabled={busy || code.length < 4} className="w-full">
                {busy ? <Loader className="w-4 h-4 animate-spin" /> : 'Verify'}
              </Button>
              <button type="button" onClick={() => setMode('phone')} className="text-slate-400 text-sm underline">
                Use another number
              </button>
            </form>
          )}

          {mode === 'pin_setup' && (
            <form onSubmit={setFirstPin} className="space-y-4">
              <p className="text-slate-300 text-sm">First sign-in: create your PIN (4-8 digits).</p>
              <Input type="password" inputMode="numeric" value={pin} autoFocus required maxLength={8} placeholder="New PIN"
                     onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
                     className={`${field} text-center text-2xl tracking-widest`} data-testid="staff-new-pin" />
              <Input type="password" inputMode="numeric" value={pin2} required maxLength={8} placeholder="Repeat PIN"
                     onChange={(e) => setPin2(e.target.value.replace(/\D/g, '').slice(0, 8))}
                     className={`${field} text-center text-2xl tracking-widest`} data-testid="staff-confirm-pin" />
              <Button type="submit" disabled={busy || pin.length < 4 || pin2.length < 4} className="w-full">
                {busy ? <Loader className="w-4 h-4 animate-spin" /> : 'Create PIN & sign in'}
              </Button>
            </form>
          )}

          {(mode === 'pin_new' || mode === 'pin_trusted') && (
            <form onSubmit={signInWithPin} className="space-y-4">
              {mode === 'pin_trusted' && (
                <div className="flex items-center gap-2 text-emerald-300 text-sm">
                  <ShieldCheck className="w-4 h-4" /> Trusted device
                </div>
              )}
              <div>
                <Label className="text-slate-200 flex items-center gap-2 mb-2"><Lock className="w-4 h-4" />Your PIN</Label>
                <Input type="password" inputMode="numeric" value={pin} autoFocus required maxLength={6}
                       onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                       className={`${field} text-center text-3xl tracking-widest`} data-testid="staff-pin" />
              </div>
              <Button type="submit" disabled={busy || pin.length < 4} className="w-full">
                {busy ? <Loader className="w-4 h-4 animate-spin" /> : 'Sign in'}
              </Button>
              {mode === 'pin_trusted' && (
                <button type="button" onClick={useAnotherPhone} className="text-slate-400 text-sm underline">
                  Not you? Use another phone
                </button>
              )}
            </form>
          )}

          {error && (
            <div className="p-3 rounded bg-red-500/10 text-red-300 text-sm flex items-center gap-2" role="alert">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
            </div>
          )}

          <p className="text-slate-500 text-xs">
            No PIN yet? Your manager turns on POS access for you; the set-up link comes by text.
          </p>
        </div>
      </div>
    </div>
  );
}
